import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { TextField, FormHelperText, Box, useTheme } from '@mui/material';
import {
  getModernFieldSurface,
  getModernTextFieldSx,
} from '../pages/drawer/jobs/modernFormFieldStyles';

const GooglePlacesAutocomplete = ({ 
  value, 
  onChange, 
  onPlaceSelect,
  error,
  helperText,
  label = "Address",
  placeholder = "Start typing an address...",
  size = "medium",
  sx = {},
  onBlur,
  name,
  required = false,
  id,
  ...props 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const surface = getModernFieldSurface(isDarkMode);
  const acceptedValue = value || '';
  const [searchText, setSearchText] = useState(acceptedValue);
  const [autocomplete, setAutocomplete] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const acceptedValueRef = useRef(acceptedValue);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  acceptedValueRef.current = acceptedValue;

  // Sync search text when the accepted value changes externally
  useEffect(() => {
    setSearchText(acceptedValue);
  }, [acceptedValue]);

  // Handle when autocomplete instance is loaded
  const onLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const commitPlaceSelection = (place) => {
    if (!place?.place_id || !place.formatted_address) {
      return;
    }

    const addressData = parseAddressComponents(place);
    const displayAddress = addressData.address || addressData.formattedAddress || place.formatted_address;

    setSearchText(displayAddress);

    if (onChange) {
      onChange(displayAddress);
    }

    if (onPlaceSelect) {
      onPlaceSelect(addressData);
    }
  };

  // Handle when place is selected from the dropdown
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      commitPlaceSelection(autocomplete.getPlace());
    }
  };

  // Parse address components from Google Places result
  const parseAddressComponents = (place) => {
    const addressData = {
      address: '', // Will be set to street address (number + route)
      formattedAddress: place.formatted_address || '', // Full formatted address
      city: '',
      state: '',
      zipCode: '',
      streetNumber: '',
      route: '',
      latitude: null,
      longitude: null,
    };

    if (place.address_components) {
      place.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes('street_number')) {
          addressData.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          addressData.route = component.long_name;
        }
        if (types.includes('locality')) {
          addressData.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          addressData.state = component.short_name; // Use short_name for state code (e.g., "CA")
        }
        if (types.includes('postal_code')) {
          addressData.zipCode = component.long_name;
        }
      });

      // Combine street number and route for street address
      if (addressData.streetNumber && addressData.route) {
        addressData.address = `${addressData.streetNumber} ${addressData.route}`;
      } else if (addressData.route) {
        addressData.address = addressData.route;
      } else {
        // Fallback to formatted address if no street components found
        addressData.address = addressData.formattedAddress;
      }
    } else {
      // If no address components, use formatted address
      addressData.address = addressData.formattedAddress;
    }
    console.log('place geometry', place.geometry);
    // Extract coordinates from place geometry
    if (place.geometry && place.geometry.location) {
      const location = place.geometry.location;
      // Handle both LatLng object (with lat/lng methods) and plain object
      if (typeof location.lat === 'function') {
        addressData.latitude = location.lat();
        addressData.longitude = location.lng();
      } else {
        addressData.latitude = location.lat;
        addressData.longitude = location.lng;
      }
    }

    return addressData;
  };

  // Allow typing to filter suggestions without committing a value
  const handleInputChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleBlur = (event) => {
    setIsFocused(false);
    event.target.style.borderColor = error ? '#d32f2f' : surface.fieldBorder;
    event.target.style.borderWidth = '1px';

    // Delay so Google Places can fire onPlaceChanged when clicking a suggestion
    setTimeout(() => {
      const currentAccepted = acceptedValueRef.current;

      setSearchText((currentSearchText) => {
        const trimmedSearchText = currentSearchText.trim();

        if (trimmedSearchText === '') {
          if (currentAccepted !== '') {
            onChange?.('');
          }
          return '';
        }

        if (trimmedSearchText !== currentAccepted) {
          return currentAccepted;
        }

        return currentSearchText;
      });
      onBlurRef.current?.();
    }, 200);
  };

  const defaultSx = getModernTextFieldSx(isDarkMode, sx);

  // Check if Google Maps API is loaded
  const isGoogleMapsLoaded = typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places;

  if (!isGoogleMapsLoaded) {
    return (
      <TextField
        fullWidth
        required={required}
        name={name}
        id={id}
        label={label}
        value={searchText}
        onChange={handleInputChange}
        onBlur={onBlur}
        error={error}
        helperText={helperText || 'Loading Google Maps...'}
        placeholder={placeholder}
        disabled
        multiline
        rows={2}
        sx={defaultSx}
        {...props}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Autocomplete
        onLoad={onLoad}
        onPlaceChanged={onPlaceChanged}
        restrictions={{ country: 'us' }}
      >
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          placeholder={!isFocused && !searchText ? placeholder : ''}
          style={{
            width: '100%',
            padding: size === 'small' 
              ? '12.5px 14px'
              : (isFocused || searchText) ? '18px 14px 6px 14px' : '16.5px 14px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            border: `1px solid ${error ? '#d32f2f' : surface.fieldBorder}`,
            borderRadius: '12px',
            backgroundColor: surface.fieldBg,
            boxShadow: isDarkMode
              ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
              : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 2px rgba(15,23,42,0.04)',
            color: isDarkMode ? theme.palette.text.primary : '#0f172a',
            outline: 'none',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, padding 0.2s',
            height: size === 'small' ? '40px' : '56px',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            verticalAlign: 'middle',
            backdropFilter: 'blur(10px)',
          }}
          onFocus={(e) => {
            setIsFocused(true);
            e.target.style.borderColor = error ? '#d32f2f' : surface.accent;
            e.target.style.borderWidth = '1.5px';
          }}
          onBlur={handleBlur}
          onMouseEnter={(e) => {
            if (document.activeElement !== e.target) {
              e.target.style.borderColor = error ? '#d32f2f' : surface.accent;
            }
          }}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.target) {
              e.target.style.borderColor = error ? '#d32f2f' : surface.fieldBorder;
            }
          }}
        />
      </Autocomplete>
      {label && (
        <Box
          component="label"
          sx={{
            position: 'absolute',
            top: size === 'small' 
              ? ((isFocused || searchText) ? '-8px' : '12px')
              : ((isFocused || searchText) ? '-8px' : '14px'),
            left: '14px',
            fontSize: (isFocused || searchText) ? '0.75rem' : (size === 'small' ? '0.875rem' : '0.875rem'),
            fontWeight: 600,
            color: error ? '#d32f2f' : ((isFocused || searchText) ? surface.accent : surface.muted),
            backgroundColor: surface.fieldBg,
            padding: (isFocused || searchText) ? '0 4px' : '0',
            transition: 'all 0.2s',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {label}{required ? ' *' : ''}
        </Box>
      )}
      {helperText && (
        <FormHelperText 
          error={error}
          sx={{ 
            fontSize: '0.75rem',
            fontWeight: 500,
            mt: 0.5,
            mx: 1.75,
          }}
        >
          {helperText}
        </FormHelperText>
      )}
      {/* Ensure Google's autocomplete dropdown has proper z-index */}
      <style>
        {`
          .pac-container {
            z-index: 1300 !important;
            font-family: inherit;
            background-color: ${surface.fieldBg} !important;
            border: 1px solid ${surface.panelBorder} !important;
            border-radius: 12px !important;
          }
          .pac-item {
            padding: 8px 12px;
            font-size: 0.875rem;
            cursor: pointer;
            color: ${isDarkMode ? theme.palette.text.primary : '#0f172a'} !important;
          }
          ${isDarkMode ? `
          .pac-item .pac-item-query,
          .pac-item .pac-matched,
          .pac-item .pac-item-query .pac-matched {
            color: inherit !important;
          }
          ` : ''}
          .pac-item:hover {
            background-color: ${isDarkMode ? '#1e293b' : '#f8fafc'} !important;
          }
          .pac-item-selected {
            background-color: ${isDarkMode ? '#334155' : 'rgba(15, 23, 42, 0.06)'} !important;
          }
        `}
      </style>
    </Box>
  );
};

export default GooglePlacesAutocomplete;

